import { unwrap, type Schemas } from '@mrigl/api-client';
import type { MessageKey } from '@mrigl/i18n';
import { pickLocalized, type Locale } from '@mrigl/shared';
import { Alert, Button, Card, SelectField } from '@mrigl/ui';
import { useId, useState, type ChangeEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../api.js';
import { Loading } from '../components.js';
import {
  GridBody,
  GridToolbar,
  Pagination,
  SearchFilter,
  SortHeader,
  useGridState,
  usePagedData,
  useRowLink,
  type ListQuery,
} from '../grid.js';
import { useI18n, type I18n } from '../i18n.js';

type Merchant = Schemas['OperatorMerchant'];
type StoreRow = Merchant['stores'][number];
type ImportReport = Schemas['MerchantImportReport'];
type MerchantsQuery = ListQuery<'/operator-api/v1/merchants'>;
type StoresFilter = NonNullable<MerchantsQuery['stores']>;
type PresenceFilter = NonNullable<MerchantsQuery['presence']>;
type Sort = NonNullable<MerchantsQuery['sort']>;

const STORE_FILTERS: readonly StoresFilter[] = ['any', 'one', 'several'];
const PRESENCE_FILTERS: readonly PresenceFilter[] = ['any', 'unpublished', 'suspended', 'live'];

function storeName(store: StoreRow, locale: Locale): string {
  return pickLocalized(store.name, locale, 'fr') ?? store.slug;
}

function ownerName(merchant: Merchant, empty = merchant.email): string {
  const name = merchant.displayName?.trim();
  return name === undefined || name === '' ? empty : name;
}

const IMPORT_ERROR: Record<ImportReport['errors'][number]['code'], MessageKey> = {
  'invalid-csv': 'admin.catalog.importError.invalid-csv',
  'no-rows': 'admin.catalog.importError.no-rows',
  'unknown-column': 'admin.catalog.importError.unknown-column',
  'missing-column': 'admin.catalog.importError.missing-column',
  'too-many-rows': 'admin.catalog.importError.too-many-rows',
  required: 'admin.catalog.importError.required',
  invalid: 'admin.catalog.importError.invalid',
  'duplicate-email': 'admin.operator.merchants.importError.duplicate-email',
  'operator-account': 'admin.operator.merchants.importError.operator-account',
};

function importErrorText(t: I18n['t'], item: ImportReport['errors'][number]): string {
  const message = t(IMPORT_ERROR[item.code]);
  return item.column === null
    ? t('admin.catalog.importErrorLine', { line: item.line, message })
    : t('admin.catalog.importErrorCell', { line: item.line, column: item.column, message });
}

function exportHref(
  filters: { q: string; stores: string; presence: string },
  sort: string,
): string {
  const params = new URLSearchParams({ sort, pageSize: '100' });
  if (filters.q !== '') params.set('q', filters.q);
  if (filters.stores !== 'any') params.set('stores', filters.stores);
  if (filters.presence !== 'any') params.set('presence', filters.presence);
  return `/operator-api/v1/merchants/export?${params.toString()}`;
}

/** Merchant card on a store: owner, locale, sibling stores. */
export function OperatorStoreMerchantCard({ storeId }: { readonly storeId: string }) {
  const { t, locale } = useI18n();
  const { data, loading, error } = usePagedData<Merchant>(
    storeId,
    async () => {
      const merchant = await unwrap(
        api.GET('/operator-api/v1/stores/{storeId}/merchant', {
          params: { path: { storeId } },
        }),
      );
      return { items: [merchant], page: 1, pageSize: 1, total: 1 };
    },
    () => undefined,
  );
  if (loading && data === null) return <Loading />;
  if (error !== null || data?.items[0] === undefined) return null;
  const merchant = data.items[0];
  const others = merchant.stores.filter((store) => store.storeId !== storeId);
  return (
    <Card labelledBy="operator-merchant">
      <h2 id="operator-merchant" className="mrigl-subheading">
        {t('admin.operator.merchants.card')}
      </h2>
      <p>
        <Link to={`/merchants/${merchant.merchantAccountId}`} dir="auto">
          {ownerName(merchant)}
        </Link>
      </p>
      <p className="mrigl-muted mrigl-ltr">{merchant.email}</p>
      <p className="mrigl-muted">{t(`admin.language.${merchant.preferredLocale}`)}</p>
      <p>
        {t('admin.operator.merchants.storeCount')}: {merchant.storeCount}
      </p>
      {others.length === 0 ? null : (
        <p>
          {t('admin.operator.merchants.otherStores')}{' '}
          {others.map((store, index) => (
            <span key={store.storeId}>
              {index === 0 ? null : ', '}
              <Link to={`/stores/${store.storeId}`} dir="auto">
                {storeName(store, locale)}
              </Link>
            </span>
          ))}
        </p>
      )}
    </Card>
  );
}

/** Directory of merchant accounts: search, filters, export, import. */
export function OperatorMerchantsPage() {
  const { t, formatDateTime } = useI18n();
  const grid = useGridState({
    sort: '-createdAt',
    sortable: ['createdAt', 'email', 'storeCount'],
    filters: { q: '', stores: 'any', presence: 'any' },
  });
  const rowLink = useRowLink();
  const { filters } = grid;
  const key = JSON.stringify([grid.page, grid.pageSize, grid.sort, filters]);
  const { data, loading, error, reload } = usePagedData<Merchant>(
    key,
    () =>
      unwrap(
        api.GET('/operator-api/v1/merchants', {
          params: {
            query: {
              page: grid.page,
              pageSize: grid.pageSize,
              sort: grid.sort as Sort,
              stores: filters.stores as StoresFilter,
              presence: filters.presence as PresenceFilter,
              ...(filters.q === '' ? {} : { q: filters.q }),
            },
          },
        }),
      ),
    grid.setPage,
  );

  return (
    <div className="mrigl-stack">
      <div className="admin-pagehead">
        <h1 className="mrigl-heading">{t('admin.operator.merchants')}</h1>
      </div>
      <p className="mrigl-muted">{t('admin.operator.merchants.intro')}</p>
      <GridToolbar
        activeFilters={grid.activeFilters}
        onReset={grid.reset}
        search={
          <SearchFilter
            label={t('admin.operator.merchants.search')}
            value={filters.q}
            onChange={(value) => {
              grid.setFilter('q', value);
            }}
          />
        }
      >
        <SelectField
          label={t('admin.operator.merchants.stores')}
          value={filters.stores}
          onChange={(event) => {
            grid.setFilter('stores', event.target.value);
          }}
          options={STORE_FILTERS.map((value) => ({
            value,
            label: t(`admin.operator.merchants.stores.${value}`),
          }))}
        />
        <SelectField
          label={t('admin.operator.merchants.presence')}
          value={filters.presence}
          onChange={(event) => {
            grid.setFilter('presence', event.target.value);
          }}
          options={PRESENCE_FILTERS.map((value) => ({
            value,
            label: t(`admin.operator.merchants.presence.${value}`),
          }))}
        />
      </GridToolbar>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {data === null ? (
        loading ? (
          <Loading />
        ) : null
      ) : (
        <>
          <GridBody
            loading={loading}
            empty={data.items.length === 0}
            filtered={grid.activeFilters > 0}
            emptyText={t('admin.operator.merchants.empty')}
          >
            <table className="mrigl-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.account.displayName')}</th>
                  <SortHeader
                    field="email"
                    label={t('admin.account.email')}
                    sort={grid.sort}
                    onSort={grid.setSort}
                  />
                  <SortHeader
                    field="storeCount"
                    label={t('admin.operator.merchants.storeCount')}
                    sort={grid.sort}
                    onSort={grid.setSort}
                    numeric
                    firstDescending
                  />
                  <th scope="col">{t('admin.operator.stores')}</th>
                  <SortHeader
                    field="createdAt"
                    label={t('admin.operator.merchants.created')}
                    sort={grid.sort}
                    onSort={grid.setSort}
                    firstDescending
                  />
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => {
                  const href = `/merchants/${item.merchantAccountId}`;
                  return (
                    <tr key={item.merchantAccountId} {...rowLink(href)}>
                      <td>
                        <Link to={href} dir="auto">
                          {ownerName(item, t('admin.operator.merchants.noName'))}
                        </Link>
                      </td>
                      <td className="mrigl-ltr">{item.email}</td>
                      <td>{item.storeCount}</td>
                      <td dir="auto">{item.storeSlugs.slice(0, 3).join(', ')}</td>
                      <td className="admin-nowrap">{formatDateTime(item.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </GridBody>
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={grid.setPage}
            onPageSize={grid.setPageSize}
          />
        </>
      )}
      <CsvSection href={exportHref(filters, grid.sort)} onImported={reload} />
    </div>
  );
}

function CsvSection({
  href,
  onImported,
}: {
  readonly href: string;
  readonly onImported: () => void;
}) {
  const { t } = useI18n();
  const inputId = useId();
  const [busy, setBusy] = useState(false);
  const [csv, setCsv] = useState<string | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = (body: string, dryRun: boolean) =>
    unwrap(
      api.POST('/operator-api/v1/merchants/import', {
        params: { query: { dryRun: dryRun ? 'true' : 'false' } },
        body,
        bodySerializer: (value: string) => value,
        headers: { 'content-type': 'text/csv' },
      }),
    );

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (file === undefined) return;
    setReport(null);
    setError(null);
    setStatus(t('admin.catalog.importChecking'));
    setBusy(true);
    try {
      const text = await file.text();
      setCsv(text);
      setReport(await send(text, true));
      setStatus(null);
    } catch (failure) {
      setStatus(null);
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    if (csv === null) return;
    setBusy(true);
    setError(null);
    try {
      const result = await send(csv, false);
      if (result.errors.length > 0) {
        setReport(result);
      } else {
        setReport(null);
        setCsv(null);
        setStatus(
          t('admin.operator.merchants.importDone', {
            created: result.created,
            updated: result.updated,
          }),
        );
        onImported();
      }
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mrigl-card mrigl-stack" aria-labelledby={`${inputId}-title`}>
      <h2 id={`${inputId}-title`} className="mrigl-subheading">
        {t('admin.operator.merchants.csv')}
      </h2>
      <a href={href} download>
        {t('admin.operator.merchants.export')}
      </a>
      <label className="admin-upload" htmlFor={inputId}>
        <span className="mrigl-button">{t('admin.operator.merchants.import')}</span>
        <input
          id={inputId}
          className="admin-upload__input"
          type="file"
          accept=".csv,text/csv"
          disabled={busy}
          aria-describedby={`${inputId}-hint`}
          onChange={(event) => {
            void onFile(event);
          }}
        />
      </label>
      <p id={`${inputId}-hint`} className="mrigl-muted">
        {t('admin.operator.merchants.importHint')}
      </p>
      <div role="status">{status === null ? null : <p>{status}</p>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {report === null ? null : report.errors.length > 0 ? (
        <Alert tone="error">
          <p>{t('admin.catalog.importErrors')}</p>
          <ul className="mrigl-list">
            {report.errors.map((item) => (
              <li key={`${String(item.line)}-${item.column ?? ''}-${item.code}`}>
                {importErrorText(t, item)}
              </li>
            ))}
          </ul>
        </Alert>
      ) : (
        <div className="mrigl-stack">
          <p>
            {t('admin.operator.merchants.importSummary', {
              created: report.created,
              updated: report.updated,
            })}
          </p>
          <Button busy={busy} onClick={() => void apply()}>
            {t('admin.catalog.importApply')}
          </Button>
        </div>
      )}
    </section>
  );
}

/** One merchant and the stores they own. */
export function OperatorMerchantPage() {
  const { merchantId = '' } = useParams();
  const { t, locale, formatDateTime } = useI18n();
  const { data, loading, error } = usePagedData<Merchant>(
    merchantId,
    async () => {
      const merchant = await unwrap(
        api.GET('/operator-api/v1/merchants/{merchantId}', {
          params: { path: { merchantId } },
        }),
      );
      return { items: [merchant], page: 1, pageSize: 1, total: 1 };
    },
    () => undefined,
  );
  if (error !== null) return <Alert tone="error">{error}</Alert>;
  if (loading || data?.items[0] === undefined) return <Loading />;
  const merchant = data.items[0];
  return (
    <div className="mrigl-stack">
      <p>
        <Link className="admin-back" to="/merchants">
          {t('admin.operator.merchants')}
        </Link>
      </p>
      <div className="admin-pagehead">
        <h1 className="mrigl-heading" dir="auto">
          {ownerName(merchant)}
        </h1>
        <p className="mrigl-muted mrigl-ltr">{merchant.email}</p>
      </div>
      <p className="mrigl-muted">
        {t(`admin.language.${merchant.preferredLocale}`)} · {formatDateTime(merchant.createdAt)} ·{' '}
        {t('admin.operator.merchants.storeCount')}: {merchant.storeCount}
      </p>
      {merchant.stores.length === 0 ? (
        <p className="mrigl-muted">{t('admin.operator.noStores')}</p>
      ) : (
        <table className="mrigl-table">
          <thead>
            <tr>
              <th scope="col">{t('admin.operator.stores')}</th>
              <th scope="col">{t('admin.store.slug')}</th>
              <th scope="col">{t('admin.order.status')}</th>
              <th scope="col">{t('admin.operator.merchants.created')}</th>
            </tr>
          </thead>
          <tbody>
            {merchant.stores.map((store) => (
              <tr key={store.storeId}>
                <td>
                  <Link to={`/stores/${store.storeId}`} dir="auto">
                    {storeName(store, locale)}
                  </Link>
                </td>
                <td className="admin-code mrigl-ltr">{store.slug}</td>
                <td>{t(`admin.storeStatus.${store.status}` as MessageKey)}</td>
                <td className="admin-nowrap">{formatDateTime(store.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
